import { apiClient } from './apiClient';
import type { 
  PlotPhoto, 
  BulkUploadPhotoRequest, 
  BulkUploadPhotoResponse 
} from '../../types/audit';

/**
 * Error response structure from API
 */
interface ApiError {
  message: string;
  status?: number;
  originalError?: any;
}

/**
 * Photo API Service
 * Handles all plot photo-related API operations
 */
export class PhotoService {
  /**
   * Get all photos for a specific audit
   * 
   * @param auditId - The ID of the audit
   * @returns Promise with array of plot photos
   * 
   * @example
   * ```typescript
   * const photos = await photoService.getPhotosByAudit(123);
   * ```
   */
  async getPhotosByAudit(auditId: number): Promise<PlotPhoto[]> {
    try {
      const response = await apiClient.get<PlotPhoto[]>(
        `/plot-photos/?audit_id=${auditId}`
      );
      
      return response.data;
    } catch (error: any) {
      throw this.handleError(error, 'error.photo.fetch_failed');
    }
  }

  /**
   * Get all photos for a specific audit plot
   * 
   * @param auditPlotId - The ID of the audit plot
   * @returns Promise with array of plot photos
   * 
   * @example
   * ```typescript
   * const photos = await photoService.getPhotosByAuditPlot('uuid-123');
   * ```
   */
  async getPhotosByAuditPlot(auditPlotId: string): Promise<PlotPhoto[]> {
    try {
      const response = await apiClient.get<any>(
        `/plot-photos/?audit_plot_id=${auditPlotId}`
      );
      
      // Handle paginated response
      if (response.data && typeof response.data === 'object' && 'results' in response.data) {
        return Array.isArray(response.data.results) ? response.data.results : [];
      }
      
      // Handle direct array response
      if (Array.isArray(response.data)) {
        return response.data;
      }
      
      // Fallback to empty array
      console.warn('Unexpected response format for audit plot photos:', response.data);
      return [];
    } catch (error: any) {
      throw this.handleError(error, 'error.photo.fetch_failed');
    }
  }

  /**
   * Get all photos for a specific control point
   * 
   * @param controlPointId - The ID of the control point
   * @returns Promise with array of plot photos
   * 
   * @example
   * ```typescript
   * const photos = await photoService.getPhotosByControlPoint('uuid-123');
   * ```
   */
  async getPhotosByControlPoint(controlPointId: string): Promise<PlotPhoto[]> {
    try {
      const response = await apiClient.get<any>(
        `/plot-photos/?control_point_id=${controlPointId}`
      );
      
      // Handle paginated response
      if (response.data && typeof response.data === 'object' && 'results' in response.data) {
        return Array.isArray(response.data.results) ? response.data.results : [];
      }
      
      // Handle direct array response
      if (Array.isArray(response.data)) {
        return response.data;
      }
      
      // Fallback to empty array
      console.warn('Unexpected response format for control point photos:', response.data);
      return [];
    } catch (error: any) {
      throw this.handleError(error, 'error.photo.fetch_failed');
    }
  }

  /**
   * Get all photos for a specific adjacent crop
   * 
   * @param adjacentCropId - The ID of the adjacent crop
   * @returns Promise with array of plot photos
   * 
   * @example
   * ```typescript
   * const photos = await photoService.getPhotosByAdjacentCrop('uuid-123');
   * ```
   */
  async getPhotosByAdjacentCrop(adjacentCropId: string): Promise<PlotPhoto[]> {
    try {
      const response = await apiClient.get<any>(
        `/plot-photos/?adjacent_crop_id=${adjacentCropId}`
      );
      
      // Handle paginated response
      if (response.data && typeof response.data === 'object' && 'results' in response.data) {
        return Array.isArray(response.data.results) ? response.data.results : [];
      }
      
      // Handle direct array response
      if (Array.isArray(response.data)) {
        return response.data;
      }
      
      // Fallback to empty array
      console.warn('Unexpected response format for adjacent crop photos:', response.data);
      return [];
    } catch (error: any) {
      throw this.handleError(error, 'error.photo.fetch_failed');
    }
  }

  /**
   * Upload a single photo
   * 
   * @param auditId - The ID of the audit
   * @param auditPlotId - The ID of the audit plot
   * @param file - The image file to upload
   * @param description - Optional description for the photo
   * @returns Promise with the created plot photo
   * 
   * @example
   * ```typescript
   * const photo = await photoService.uploadPhoto(
   *   123,
   *   'uuid-456',
   *   imageFile,
   *   'Vista frontal de la parcela'
   * );
   * ```
   */
  async uploadPhoto(
    auditId: number,
    auditPlotId: string,
    file: File,
    description?: string
  ): Promise<PlotPhoto> {
    return this.uploadPhotoForEntity(
      auditId,
      { type: "audit_plot", id: auditPlotId },
      file,
      description,
    );
  }

  /**
   * Upload a photo for an arbitrary entity (audit_plot / control_point_review /
   * adjacent_crop_review). The backend `PlotPhotoSerializer` accepts exactly
   * one of the three FKs so we route via `target.type` here.
   */
  async uploadPhotoForEntity(
    auditId: number,
    target:
      | { type: "audit_plot"; id: string }
      | { type: "control_point_review"; id: string }
      | { type: "adjacent_crop_review"; id: string },
    file: File,
    description?: string,
  ): Promise<PlotPhoto> {
    try {
      this.validateImageFile(file);
      const base64Image = await this.fileToBase64(file);
      const payload: Record<string, unknown> = {
        audit: auditId,
        image_base64: base64Image,
        description: description || "",
      };
      payload[target.type] = target.id;

      const response = await apiClient.post<PlotPhoto>(
        "/plot-photos/",
        payload,
        { headers: { "Content-Type": "application/json" } },
      );
      return response.data;
    } catch (error: any) {
      throw this.handleError(error, "error.photo.upload_failed");
    }
  }

  /**
   * Upload multiple photos at once
   * 
   * @param request - Bulk upload request containing audit ID, images, plot IDs, and optional descriptions
   * @returns Promise with bulk upload response containing created photos and errors
   * 
   * @example
   * ```typescript
   * const result = await photoService.bulkUploadPhotos({
   *   audit_id: 123,
   *   images: [file1, file2, file3],
   *   plot_ids: ['uuid-1', 'uuid-2', 'uuid-3'],
   *   descriptions: ['Photo 1', 'Photo 2', 'Photo 3']
   * });
   * 
   * console.log(`Created: ${result.total_created}, Errors: ${result.total_errors}`);
   * ```
   */
  async bulkUploadPhotos(
    request: BulkUploadPhotoRequest
  ): Promise<BulkUploadPhotoResponse> {
    try {
      // Validate that images and plot_ids have the same length
      if (request.images.length !== request.plot_ids.length) {
        throw new Error('Number of images must match number of plot IDs');
      }

      // Validate all image files
      request.images.forEach((file, index) => {
        try {
          this.validateImageFile(file);
        } catch (error: any) {
          throw new Error(`Image ${index + 1}: ${error.message}`);
        }
      });

      // Convert all files to base64 and create photos array
      const photosPromises = request.images.map(async (file, index) => {
        const base64 = await this.fileToBase64(file);
        return {
          audit_plot_id: request.plot_ids[index],
          image_base64: base64,
          image_name: file.name,
          description: request.descriptions?.[index] || '',
        };
      });

      const photos = await Promise.all(photosPromises);

      // Create JSON payload with photos array
      const payload = {
        audit_id: request.audit_id,
        photos: photos,
      };

      const response = await apiClient.post<BulkUploadPhotoResponse>(
        '/plot-photos/bulk-upload/',
        payload,
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );
      
      return response.data;
    } catch (error: any) {
      throw this.handleError(error, 'error.photo.bulk_upload_failed');
    }
  }

  /**
   * Delete a photo
   * 
   * @param photoId - The ID of the photo to delete
   * @returns Promise that resolves when deletion is complete
   * 
   * @example
   * ```typescript
   * await photoService.deletePhoto('uuid-123');
   * ```
   */
  async deletePhoto(photoId: string): Promise<void> {
    try {
      await apiClient.delete(`/plot-photos/${photoId}/`);
    } catch (error: any) {
      throw this.handleError(error, 'error.photo.delete_failed');
    }
  }

  /**
   * Convert a File object to base64 string
   * 
   * @param file - The file to convert
   * @returns Promise with base64 string (without data URL prefix)
   */
  private fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = () => {
        const result = reader.result as string;
        // Remove the data URL prefix (e.g., "data:image/jpeg;base64,")
        const base64 = result.split(',')[1];
        resolve(base64);
      };
      
      reader.onerror = () => {
        reject(new Error('Failed to read file'));
      };
      
      reader.readAsDataURL(file);
    });
  }

  /**
   * Validate image file type and size
   * 
   * @param file - The file to validate
   * @throws Error if file is invalid
   */
  private validateImageFile(file: File): void {
    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
    if (!allowedTypes.includes(file.type)) {
      throw new Error('Only JPG, JPEG, and PNG images are allowed');
    }

    // Validate file size (max 10MB)
    const maxSize = 10 * 1024 * 1024; // 10MB in bytes
    if (file.size > maxSize) {
      throw new Error('Image file size cannot exceed 10MB');
    }
  }

  /**
   * Handle API errors and provide translated error messages
   * 
   * @param error - The error object from the API
   * @param defaultMessageKey - Default translation key for the error message
   * @returns Formatted error object
   */
  private handleError(error: any, defaultMessageKey: string): ApiError {
    // If error already has a message property (from apiClient interceptor)
    if (error.message) {
      return {
        message: error.message,
        status: error.status,
        originalError: error.originalError,
      };
    }

    // Handle axios error response
    if (error.response?.data) {
      const errorData = error.response.data;
      
      // Extract error message from various possible formats
      let message = defaultMessageKey;
      
      if (typeof errorData === 'string') {
        message = errorData;
      } else if (errorData.error) {
        message = errorData.error;
      } else if (errorData.detail) {
        message = errorData.detail;
      } else if (errorData.message) {
        message = errorData.message;
      } else if (errorData.errors) {
        // Handle validation errors
        if (Array.isArray(errorData.errors)) {
          message = errorData.errors.join(', ');
        } else if (typeof errorData.errors === 'object') {
          message = Object.values(errorData.errors).flat().join(', ');
        }
      }

      return {
        message,
        status: error.response.status,
        originalError: error,
      };
    }

    // Handle network or other errors
    return {
      message: error.message || defaultMessageKey,
      originalError: error,
    };
  }
}

/**
 * Singleton instance of PhotoService
 */
export const photoService = new PhotoService();

export default photoService;
